import { ref, watch, onScopeDispose, type Ref } from 'vue'
import type { TourRect, TourSpotlightShape } from '../types/tour.types'
import { TOUR_CONFIG } from '../config/tour.config'

/**
 * Tracks the on-screen rectangle of the current target element and keeps it in
 * sync through scroll / resize / layout changes. A single ResizeObserver plus
 * rAF-throttled scroll+resize listeners are attached only while a target exists
 * and are torn down otherwise — nothing runs while the tour is idle.
 */
export function useSpotlight(
  targetEl: Ref<Element | null>,
  shape: Ref<TourSpotlightShape>,
  padding: Ref<number>,
) {
  const rect = ref<TourRect | null>(null)

  let resizeObserver: ResizeObserver | null = null
  let rafId = 0
  let listening = false

  function measure(): void {
    const el = targetEl.value
    if (!el) {
      rect.value = null
      return
    }
    const r = el.getBoundingClientRect()
    const pad = padding.value
    let radius: number
    switch (shape.value) {
      case 'rect':
        radius = 0
        break
      case 'circle':
        radius = Math.max(r.width, r.height) / 2 + pad
        break
      case 'rounded':
        radius = TOUR_CONFIG.spotlightRadius + pad
        break
      default: {
        const css = parseFloat(getComputedStyle(el).borderRadius) || TOUR_CONFIG.spotlightRadius
        radius = css + pad
      }
    }
    rect.value = {
      top: r.top - pad,
      left: r.left - pad,
      width: r.width + pad * 2,
      height: r.height + pad * 2,
      radius,
    }
  }

  function scheduleMeasure(): void {
    if (rafId) return
    rafId = requestAnimationFrame(() => {
      rafId = 0
      measure()
    })
  }

  function scrollIntoViewIfNeeded(): void {
    const el = targetEl.value
    if (!el) return
    const r = el.getBoundingClientRect()
    const fullyVisible = r.top >= 0 && r.bottom <= window.innerHeight
    if (!fullyVisible) {
      el.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'smooth' })
    }
  }

  function attach(el: Element): void {
    detach()
    resizeObserver = new ResizeObserver(scheduleMeasure)
    resizeObserver.observe(el)
    resizeObserver.observe(document.documentElement)
    window.addEventListener('scroll', scheduleMeasure, true)
    window.addEventListener('resize', scheduleMeasure)
    listening = true
    scrollIntoViewIfNeeded()
    // Re-measure shortly after scroll settles.
    measure()
    window.setTimeout(measure, 350)
  }

  function detach(): void {
    if (!listening) return
    resizeObserver?.disconnect()
    resizeObserver = null
    window.removeEventListener('scroll', scheduleMeasure, true)
    window.removeEventListener('resize', scheduleMeasure)
    if (rafId) cancelAnimationFrame(rafId)
    rafId = 0
    listening = false
  }

  watch(
    targetEl,
    (el) => {
      if (el) attach(el)
      else {
        detach()
        rect.value = null
      }
    },
    { immediate: true },
  )

  onScopeDispose(detach)

  return { rect, remeasure: measure }
}
